import { useCallback } from 'react';
import { DIALOGS_TABLE_NAME } from '@/entities/conversations';
import { PINNED_PRESET_ROUTE_PATH, useTablePresets } from '@/entities/settings';
import { useDialogsSection } from '@/pages/dialogs/model/useDialogsSection';
import { useAppNavigate } from '@/shared/lib';

/**
 * Выбор пресета в списке пресетов: в какой раздел он ведёт и к чьему виду применяется.
 *
 * Закреплённый пресет — самостоятельный раздел со своим видом, поэтому его выбор только переводит
 * в этот раздел: что там показать, решает сам раздел (`usePresetSectionSync`), и оставленные в нём
 * правки переход не переписывает. Незакреплённый пресет — обычный отбор раздела «Диалоги»:
 * он применяется к виду этого раздела, куда список и переходит.
 *
 * Снять пресет можно только в «Диалогах»: в разделе закреплённого пресета снимать нечего —
 * пресет там и есть сам раздел, поэтому снятие оттуда не предлагается (`canClearActive`).
 *
 * Новый пресет, сохранённый из вида раздела закреплённого пресета, — тоже незакреплённый отбор:
 * выбранным он становится в «Диалогах» (`savePresetAs`), и список переходит туда же.
 */
export const usePresetSelection = () => {
  const navigate = useAppNavigate();
  const { sectionKey, pinnedRoutes } = useDialogsSection(DIALOGS_TABLE_NAME);
  const { clearActive } = useTablePresets(sectionKey);
  const { apply: applyToDialogsSection } = useTablePresets(DIALOGS_TABLE_NAME);
  const isDialogsSection = sectionKey === DIALOGS_TABLE_NAME;

  const onPresetSelect = useCallback(
    (presetId: string | null) => {
      const targetRoute = presetId ? pinnedRoutes.find((route) => route.preset.id === presetId) : undefined;
      if (targetRoute) {
        navigate(PINNED_PRESET_ROUTE_PATH, { slug: targetRoute.slug, id: null });
        return;
      }
      if (presetId) {
        applyToDialogsSection(presetId);
        navigate('/dialogs/:id?', { id: null });
        return;
      }
      clearActive();
      navigate('/dialogs/:id?', { id: null });
    },
    [pinnedRoutes, navigate, applyToDialogsSection, clearActive],
  );

  const onPresetSaved = useCallback(() => {
    if (!isDialogsSection) navigate('/dialogs/:id?', { id: null });
  }, [isDialogsSection, navigate]);

  return {
    onPresetSelect,
    onPresetSaved,
    /** Снятие активного пресета предлагается только в разделе «Диалоги» */
    canClearActive: isDialogsSection,
  };
};
